/**
 * Hooks for subscribing to job updates via SSE.
 *
 * These hooks read from the Zustand store which is updated by useRealtimeSync
 * when job_* SSE events arrive. This provides real-time job progress without polling.
 *
 * Usage:
 *   const { job, isActive, progress } = useJobSSE(jobId);
 *   const entityJob = useEntityJobSSE('media_share', shareId);
 */

import { useAppStore } from "@/core/store/useAppStore";
import { JobProgressState } from "@/core/store/slices/realtimeSlice";
import { EntityType, isActiveStatus } from "@/services/jobsService";

interface UseJobSSEResult {
  job: JobProgressState | null;
  isActive: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  isCancelled: boolean;
  progress: number;
  progressMessage: string | null;
}

/**
 * Hook for subscribing to a specific job's updates via SSE.
 *
 * @param jobId - The job ID to subscribe to (can be null)
 * @returns Job state and derived status flags
 */
export function useJobSSE(jobId: string | null): UseJobSSEResult {
  const job = useAppStore((state) =>
    jobId ? state.realtime.jobs[jobId] ?? null : null
  );

  const isActive = job ? isActiveStatus(job.status) : false;
  const isCompleted = job?.status === "completed";
  const isCancelled = job?.status === "cancelled";
  const isFailed = job?.status === "failed";

  return {
    job,
    isActive,
    isCompleted,
    isFailed,
    isCancelled,
    progress: job?.progress ?? 0,
    progressMessage: job?.progressMessage ?? null,
  };
}

/**
 * Hook for subscribing to jobs for a specific entity via SSE.
 *
 * Finds the most recent job for the given entity type and ID.
 *
 * @param entityType - Type of entity (media_share, post, etc.)
 * @param entityId - ID of the entity
 * @returns The most recent job for this entity, or null
 */
export function useEntityJobSSE(
  entityType: EntityType,
  entityId: string
): JobProgressState | null {
  return useAppStore((state) => {
    const jobs = Object.values(state.realtime.jobs);
    const matchingJobs = jobs.filter(
      (j) => j.entityType === entityType && j.entityId === entityId
    );
    if (matchingJobs.length === 0) return null;
    return matchingJobs.reduce((latest, job) =>
      job.updatedAt > latest.updatedAt ? job : latest
    );
  });
}

/**
 * Hook for getting all active (running/queued) jobs.
 *
 * @returns Array of currently active jobs
 */
export function useActiveJobs(): JobProgressState[] {
  return useAppStore((state) =>
    Object.values(state.realtime.jobs).filter((j) => isActiveStatus(j.status))
  );
}
